import { useState } from "react";
import { YStack, XStack, Text } from "tamagui";
import { colors } from "@styles/colors";
import { Header, StickyBtn, Fade, RADIUS } from "./Common";
import { Ic, Ar } from "./Icons";
import { OTPInputBlocks } from "@components/form/OTPInputBlocks";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import api, { handleRequest } from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser, IdentityContext } from "@utils/store/user/useUser";
import { isPracticeMode } from "@utils/practice/practiceMode";

interface JoinResult {
  context_id: number;
  status?: string;
  message?: string;
}

interface ContextListItem {
  context_id: number;
  context_name: string;
  role?: "owner" | "member";
  primary_supplier?: { supplier_name?: string } | null;
  member_count?: number;
}

/** 把後端錯誤訊息歸類成 JoinError 能呈現的型別。 */
const mapErrorType = (msg: string): "expired" | "invalid" => {
  const m = msg.toLowerCase();
  if (m.includes("expired")) return "expired";
  return "invalid";
};

/** 從 axios error 取出後端訊息。 */
const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const res = (error as { response?: { data?: { message?: unknown; error?: unknown } } }).response;
    const data = res?.data;
    if (data) {
      if (typeof data.message === "string") return data.message;
      if (Array.isArray(data.message)) return data.message.join(", ");
      if (typeof data.error === "string") return data.error;
    }
  }
  if (error instanceof Error) return error.message;
  return "";
};

interface JoinCodeProps {
  onBack: () => void;
  onSuccess: (info: Record<string, unknown>) => void;
  onError: (type: string, code: string) => void;
  /** 未登入（無 jwt）時改走頁內「註冊＋加入」流程（JoinRegister）。 */
  onNeedRegister?: (code: string) => void;
  initialCode?: string;
}

export default function JoinCode({ onBack, onSuccess, onError, onNeedRegister, initialCode = "" }: JoinCodeProps) {
  const { cusJwtToken } = useCusAuth();
  const { setChooseIdentity } = useUser();
  const [codeVal, setCodeVal] = useState(initialCode);
  const [loading, setLoading] = useState(false);

  const ok = codeVal.length === 6;

  /** 加入後撈 contexts 清單補上帳戶名稱/瓦斯行/角色，給成功頁顯示。 */
  const enrichInfo = async (result: JoinResult): Promise<Record<string, unknown>> => {
    try {
      const res = await api.get("/app-api/customer/contexts", {
        headers: { Authorization: `Bearer ${cusJwtToken}` },
      });
      // contexts API 回 { contexts, default_context_id }（見 IdentitySwitchMenu）；
      // 防禦性兼容直接回陣列的舊形狀。
      const data = res.data?.data;
      const list = (Array.isArray(data) ? data : (data?.contexts ?? [])) as ContextListItem[];
      const joined = list.find((c) => c.context_id === result.context_id);
      if (joined) {
        // 預抓身分：把剛加入的家庭設為目前身分，「完成 → 回首頁」時 /home 立刻能抓資料。
        setChooseIdentity(joined as unknown as IdentityContext);
        return {
          name: joined.context_name,
          vendor: joined.primary_supplier?.supplier_name ?? "瓦斯行",
          role: joined.role === "owner" ? "負責人" : "成員",
          status: result.status,
          message: result.message,
          context_id: result.context_id,
        };
      }
    } catch {
      // 撈不到清單就用最小資訊呈現
    }
    return {
      name: "瓦斯帳戶",
      vendor: "瓦斯行",
      role: "成員",
      status: result.status,
      message: result.message,
      context_id: result.context_id,
    };
  };

  // completeVal：同 JoinSerial——OTPInputBlocks 的 onComplete 帶完整值進來，不能讀
  // state 的 codeVal（第 6 碼觸發時 setCodeVal 尚未 commit，閉包是 5 碼舊值）。
  // typeof 守衛擋掉按鈕 onClick 傳進來的 click 事件。
  const submit = (completeVal?: unknown) => {
    const code = typeof completeVal === "string" ? completeVal : codeVal;
    if (code.length !== 6 || loading) return;
    // 未註冊（無 jwt）：/contexts/join 一定 401，直接走頁內註冊＋加入流程。
    if (!cusJwtToken && onNeedRegister) {
      onNeedRegister(code);
      return;
    }
    handleRequest<{ data: JoinResult }>({
      asyncRequest: () =>
        api.post(
          "/app-api/customer/contexts/join",
          { invite_code: code },
          { headers: { Authorization: `Bearer ${cusJwtToken}` } },
        ),
      successCallback: async (res) => {
        const data = res.data?.data;
        if (!data?.context_id) {
          onError("invalid", code);
          return;
        }
        const info = await enrichInfo(data);
        onSuccess(info);
      },
      errorCallback: (error) => {
        onError(mapErrorType(extractErrorMessage(error)), code);
      },
      setLoading,
    });
  };

  return (
    <RadiusContentWrapper padding="0" space={0} background="transparent" contentContainerStyle={{ paddingBottom: 120 }}>
      <Header title="加入既有的瓦斯帳戶" sub="輸入對方給你的 6 位數邀請碼" onBack={onBack} />
      <YStack p="$5" gap="$4">
        <Fade i={0}>
          <YStack bg={colors.light} br={RADIUS} p="$5" style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }} ai="center" gap="$5" opacity={loading ? 0.65 : 1}>
            <YStack ai="center" gap="$1">
              <Text fontSize={12} color={colors.gray} fontWeight="700">
                邀請碼
              </Text>
              <Text fontSize={11} color={colors.gray200}>
                請邀請人在帳戶頁點「邀請成員」取得
              </Text>
            </YStack>

            {/* 練習模式：邀請碼為 6 位純數字（如 620358），用數字鍵盤、只收 0-9；
                正式維持家庭邀請碼字元集 A-Z2-9（後端 ^[A-Z2-9]{6}$）。 */}
            <OTPInputBlocks digits={6} value={codeVal} onChange={setCodeVal} onComplete={submit} alphanumeric={!isPracticeMode()} />

            {loading && (
              <XStack ai="center" gap="$2" mt="$2">
                <YStack
                  w={14}
                  h={14}
                  br={7}
                  borderWidth={2.5}
                  borderColor={colors.primary}
                  borderTopColor="transparent"
                  style={{ animation: "spin 0.8s linear infinite" }}
                />
                <Text fontSize={12} color={colors.primary} fontWeight="700">
                  驗證邀請碼中…
                </Text>
              </XStack>
            )}
          </YStack>
        </Fade>

        <Fade i={1}>
          <YStack bg={colors.primaryActive} br={RADIUS} p="$4">
            <XStack ai="center" gap="$2" mb="$2">
              <Ic d="M12 9v4M12 17h.01M21 12A9 9 0 113 12a9 9 0 0118 0z" c={colors.primary} s={15} />
              <Text fontSize={12} fontWeight="700" color={colors.primary}>
                什麼是邀請碼？
              </Text>
            </XStack>
            <Text fontSize={11} color={colors.primary} lineHeight={18}>
              家人或同事建立帳戶後，可在帳戶頁點「邀請成員」產生 6 位數碼，輸入後你就能共同管理。
            </Text>
          </YStack>
        </Fade>
        <StickyBtn
          label={loading ? "驗證中…" : ok ? "加入帳戶" : `還差 ${6 - codeVal.length} 碼`}
          disabled={!ok || loading}
          onClick={submit}
          icon={ok && !loading ? <Ar c="#fff" s={14} /> : null}
        />
        <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
      </YStack>
    </RadiusContentWrapper>
  );
}
